import Vue from 'vue'
import Vuex from 'vuex'
import store from 'store'

import { getField, updateField } from 'vuex-map-fields'
import { MessageBox } from 'element-ui'

import api from '@/api'
import c from '@/config'
import { yamlToJson } from '@/utils/jsYaml'
import H264Decoder from '@/utils/h264DecoderWebGL'

import Watcher from './watcher'

import search from './modules/search'
import label from './modules/label'
import review from './modules/review'
import history from './modules/history'
import preloading from './modules/preloading'
import mark from './modules/mark'
import concat from './modules/concat'

Vue.use(Vuex)

const storeX = new Vuex.Store({
  modules: { label, review, search, history, preloading, mark, concat },
  state: {
    // privileges
    privileges: null,

    bitRate: 20,
    // tar
    tarId: '',
    tarInfo: null,

    // camera
    cameraList: [],
    cameraIdx: -1,
    cameraInfo: {},

    // resource
    videoUrl: '',
    yamlUrl: '',
    shieldUrl: '',

    // data
    decodedVideo: {
      bufferArray: [],
      bufferWidth: 0,
      bufferHeight: 0,
    },
    detectInfo: [],
    shieldInfo: {},
    labelInfo: [],
    realtimeShieldInfo: {
      line: [], // {idx:0,time:null}
      manually: {
        col: 120,
        row: 32,
        shield: new Array(120 * 32).fill(0),
      },
    },

    // play control
    frameIdx: 0,
    totalFrameCount: 0,
    frameCount: 0, // For v2

    // layers
    showDetect: true,
    showLabel: true,
    showBlind: true,
    visiableLayers: [
      'BASE_LAYER',
      'LABEL',
      'DETECT_RESULT',
      'SHIELD_MANUALLY',
      'SHIELD_LINE',
    ],
    showRealtimeShieldManually: false, // 是否显示当前结构件屏蔽区域
    showRealtimeShieldLine: false, // 是否显示当前结构件屏蔽区域

    // control
    modified: false,

    // cache
    decodedVideoList: [],
    decodedVideoLimit: 1,

    // other
    showMagnifier: false,
    diameter: 3,

    // status
    gettingCameraList: false,
    encodingVideo: false,
    downloadingVideo: false,
    decodingVideo: false,

    alias: [],

    clothAssessmentConfig: null,

    // TOBEREMOVE
    useCache: false,
    refresh: false,

    // play control
    isPlaying: false,

    userInfo: {},

    closeDecoder: () => {},
  },
  getters: {
    getField,
    tarUrl(state) {
      if (state.tarInfo && state.tarInfo.UploadInfo === '100') {
        return state.tarInfo.OssPath
      }
      return ''
    },
    cameraId(state) {
      return state.cameraList[state.cameraIdx === -1 ? -1 : state.cameraIdx]
    },
    getDecodedVideo(state) {
      return videoUrl => {
        const cached = state.decodedVideoList.find(
          video => video.videoUrl === videoUrl,
        )
        if (cached === undefined) {
          state.decodedVideoList.shift()
          return undefined
        }
        return cached
      }
    },
    isLoading(state) {
      let loading = false
      if (c.version === '1') {
        if (state.useCache) {
          loading =
            state.gettingCameraList ||
            state.encodingVideo ||
            state.downloadingVideo
        } else {
          loading =
            state.gettingCameraList ||
            state.encodingVideo ||
            state.downloadingVideo ||
            state.decodingVideo
        }
      } else if (c.version === '2') {
        loading =
          state.gettingCameraList ||
          state.encodingVideo ||
          state.downloadingVideo
      }
      return loading
    },
  },
  mutations: {
    updateField,
    setPrivileges(state, privileges) {
      state.privileges = privileges
    },
    resetBitRate(state) {
      state.bitRate = c.defaultVideoBitRate
    },
    setTarId(state, tarId) {
      state.tarId = tarId
    },
    setTarInfo(state, tarInfo) {
      state.tarInfo = tarInfo
    },
    setVideoUrl(state, videoUrl) {
      state.videoUrl = videoUrl
    },
    setYamlUrl(state, yamlUrl) {
      state.yamlUrl = yamlUrl
    },
    setShieldUrl(state, shieldUrl) {
      state.shieldUrl = shieldUrl
    },
    setDecodedVideo(state, decodedVideo) {
      state.decodedVideo = decodedVideo
    },
    setCameraList(state, cameraList) {
      state.cameraList = cameraList
    },
    setCameraIdx(state, cameraIdx) {
      state.cameraIdx = cameraIdx
    },
    setFrameIdx(state, frameIdx) {
      state.frameIdx = frameIdx
    },
    setTotalFrameCount(state, totalFrameCount) {
      state.totalFrameCount = totalFrameCount
    },
    resetAll(state) {
      Vue.set(state, 'tarInfo', null)
      Vue.set(state, 'videoUrl', '')
      Vue.set(state, 'yamlUrl', '')
      Vue.set(state, 'shieldUrl', '')
      Vue.set(state, 'cameraList', [])
      Vue.set(state, 'cameraIdx', -1)
      Vue.set(state, 'cameraInfo', {})
      Vue.set(state, 'decodedVideo', {
        bufferArray: [],
        bufferWidth: 0,
        bufferHeight: 0,
      })
      Vue.set(state, 'detectInfo', [])
      Vue.set(state, 'shieldInfo', {})
      Vue.set(state, 'labelInfo', [])
      Vue.set(state, 'realtimeShieldInfo', {
        line: [], // {idx:0,time:null}
        manually: {
          col: 120,
          row: 32,
          shield: new Array(120 * 32).fill(0),
        },
      })
      Vue.set(state, 'refresh', !state.refresh)
    },
    setModify(state, modify) {
      state.modified = modify
    },

    addDecodedVideo(state, decodedVideo) {
      if (state.decodedVideoList.length >= state.decodedVideoLimit) {
        state.decodedVideoList.shift()
      }
      state.decodedVideoList.push(decodedVideo)
    },

    gettedCameraList(state) {
      state.gettingCameraList = false
    },
    setEncodingVideo(state, status) {
      state.encodingVideo = status
    },
    setAlias(state, alias) {
      state.alias = alias
    },
    setDetectInfo(state, detectInfo) {
      state.detectInfo = detectInfo
    },
    setShieldInfo(state, shieldInfo) {
      state.shieldInfo = shieldInfo
    },
    setLoading(state, val) {
      val === undefined && (val = true)
      state.gettingCameraList = val
      state.encodingVideo = val
      state.downloadingVideo = false
      state.decodingVideo = val
    },
    videoDownloaded(state) {
      state.downloadingVideo = false
    },
    videoDecoded(state) {
      state.decodingVideo = false
    },
    refresh(state) {
      state.refresh = !state.refresh
    },
    resetShieldInfo(state) {
      state.shieldInfo = {}
    },
  },
  actions: {
    fetchPrivileges({ comment, state, commit }) {
      return new Promise((resolve, reject) => {
        api
          .getPrivileges()
          .then(({ data }) => {
            commit('setPrivileges', data.privileges)
            resolve(data.privileges)
          })
          .catch(reject)
      })
    },
    fetchAlias({ commit }) {
      api.getAlias().then(({ data }) => {
        const aliasMap = data.AliasMap
        const alias = Object.keys(aliasMap).map(id => ({
          id,
          alias: aliasMap[id],
        }))
        commit('setAlias', alias)
      })
    },
    fetchTarInfo({ state, commit }) {
      return new Promise((resolve, reject) => {
        commit('setCameraList', [])
        commit('setCameraIdx', -1)
        commit('setLoading')
        if (!state.tarId) {
          reject(new Error('Tar ID should not be empty'))
          return
        }
        api
          .getTarInfo({ tarId: state.tarId })
          .then(({ data }) => {
            commit('setTarInfo', data)
            resolve()
          })
          .catch(reject)
      })
    },
    fetchCameraList({ state, getters: { tarUrl }, commit, dispatch }) {
      return new Promise((resolve, reject) => {
        commit('setCameraList', [])
        commit('setCameraIdx', -1)
        if (!tarUrl) {
          reject(new Error('Tar URL should not be empty'))
          return
        }
        api
          .getCameraList({ tarUrl })
          .then(({ data }) => {
            if (data.cameraIds.length > 0) {
              commit('setCameraList', data.cameraIds)
              commit('setCameraIdx', 0)
              commit('gettedCameraList')
              resolve()
            } else {
              commit('setCameraList', data.cameraIds)
              commit('setCameraIdx', '无摄像头')
              commit('setLoading', false)
            }

            // Pre fetch
            dispatch('preloading/preFetchCameraList', null, { root: true })
          })
          .catch(error => {
            commit('setLoading', false)

            // 只在点击选择数据包的时候提示是否恢复, preload不提示
            if (error.response.data.errorMessage.includes('file not found')) {
              MessageBox.confirm(
                `是否恢复（仅公司内网有效）： <br/> ${tarUrl} ？`,
                '没有找到该数据包',
                {
                  confirmButtonText: '恢复',
                  cancelButtonText: '取消',
                  dangerouslyUseHTMLString: true,
                  beforeClose: async (action, instance, done) => {
                    if (action === 'confirm') {
                      if (instance.confirmButtonText === '重新加载') {
                        dispatch('fetchCameraList')
                        done()
                      } else if (instance.confirmButtonText === '恢复') {
                        instance.confirmButtonLoading = true
                        instance.confirmButtonText = '正在恢复...'
                        await api
                          .restoreTarFile({ tarUrl })
                          .then(() => {
                            instance.title = '恢复成功'
                            instance.message = '是否重新加载？'
                            instance.confirmButtonText = '重新加载'
                          })
                          .catch(({ response: { data: { errorMessage } } }) => {
                            instance.title = '恢复失败'
                            instance.message = errorMessage
                            instance.confirmButtonText = '确定'
                          })
                        instance.confirmButtonLoading = false
                      } else {
                        done()
                      }
                    } else {
                      done()
                    }
                  },
                },
              ).catch(() => {}) // 点了取消恢复
            }
          })
      })
    },
    fetchVideoYaml({ state, commit, getters, dispatch }) {
      return new Promise((resolve, reject) => {
        commit('setEncodingVideo', true)
        if (!getters.tarUrl) {
          reject(new Error('Tar URL should not be empty'))
          return
        }
        api
          .getCameraData({
            tarUrl: getters.tarUrl,
            cameraId: getters.cameraId,
            bitrateInMbps: state.bitRate,
          })
          .then(({ data }) => {
            commit('setVideoUrl', data.videoUrl)
            commit('setYamlUrl', data.yamlUrl)
            commit('setShieldUrl', data.blindUrl)
            commit('setEncodingVideo', false)
            // Pre fetch
            dispatch('preloading/preFetchCameraData', null, { root: true })
            resolve()
          })
          .catch(reject)
      })
    },
    loadAndDecodeVideo({ state, commit }) {
      return new Promise((resolve, reject) => {
        if (!state.videoUrl) {
          commit('videoDownloaded')
          reject(new Error('Video URL should not be empty'))
          return
        }
        const url = state.videoUrl
        state.downloadingVideo = true
        // close previous decoder
        state.closeDecoder()
        state.closeDecoder = H264Decoder(
          state.videoUrl,
          () => {
            console.log('Video loaded')
            commit('videoDownloaded')
          },
          info => {
            state.decodedVideo = {
              ...info,
              bufferArray: [],
              bufferWidth: 0,
              bufferHeight: 0,
            }
            // TODO: V2 remove
            state.frameCount = info.frame
            // 返回 true 才会继续解码
            return true
          },
          (bufferArray, bufferWidth, bufferHeight, info) => {
            commit('videoDecoded')
          },
          (buffer, bufferWidth, bufferHeight, info) => {
            if (url === state.videoUrl) {
              state.decodedVideo.bufferWidth = bufferWidth
              state.decodedVideo.bufferHeight = bufferHeight
              state.decodedVideo.bufferArray.push(buffer)
            }
          },
        )
      })
    },
    fetchDetectInfo({ state, commit }) {
      return new Promise((resolve, reject) => {
        if (!state.yamlUrl) {
          reject(new Error('YAML URL should not be empty'))
          return
        }
        api.getFileContent(state.yamlUrl).then(({ data }) => {
          commit('setDetectInfo', yamlToJson(data))
        })
      })
    },
    fetchShieldInfo({ state, commit, getters }) {
      return new Promise((resolve, reject) => {
        if (!state.shieldUrl) {
          commit('resetShieldInfo')
          resolve()
          return
        }
        api.getFileContent(state.shieldUrl).then(({ data }) => {
          data.line = (data.line || []).map(idx => ({
            blindIndex: idx,
            cameraName: `camera${String(getters.cameraId).padStart(2, '0')}`,
            isPermanent: true,
          }))
          commit('setShieldInfo', data)
        })
      })
    },
    fetchClothAssessmentConfig({ state }) {
      return new Promise((resolve, reject) => {
        api
          .getFileContent(c.clothAssessmentConfigUrl)
          .then(({ data }) => {
            const clothAssessmentConfig = {}
            if (data) {
              // FIXME: 缓存配置数据，防止 strapi 挂掉
              store.set('store:clothAssessmentConfig', data)
              data.forEach(item => {
                clothAssessmentConfig[item.name] = item
              })
            }
            state.clothAssessmentConfig = clothAssessmentConfig
            resolve(clothAssessmentConfig)
          })
          .catch(() => {
            // FIXME: 缓存配置数据，防止 strapi 挂掉
            const data = store.get('store:clothAssessmentConfig')
            const clothAssessmentConfig = {}
            if (data) {
              data.forEach(item => {
                clothAssessmentConfig[item.name] = item
              })
            }
            state.clothAssessmentConfig = clothAssessmentConfig
            resolve(clothAssessmentConfig)
          })
      })
    },
    fetchRealtimeManuallyShieldConfig({ state, getters, commit }) {
      return new Promise((resolve, reject) => {
        api
          .getShieldConfig(state.tarInfo.MachineId)
          .then(({ data }) => {
            if (data.cameraShieldConfigs) {
              const shieldConfig = data.cameraShieldConfigs.find(
                item => item.cameraId === getters.cameraId,
              )
              if (shieldConfig) {
                const shieldData = JSON.parse(shieldConfig.manuallyShield)
                  .shieldAreas
                state.realtimeShieldInfo.manually.shield = shieldData.reduce(
                  (total, i) => total.concat(i),
                  [],
                )
              } else {
                state.realtimeShieldInfo.manually.shield = new Array(
                  120 * 32,
                ).fill(0)
              }
              commit('refresh')
              resolve()
            } else {
              state.realtimeShieldInfo.manually.shield = new Array(
                120 * 32,
              ).fill(0)
            }
          })
          .catch(reject)
      })
    },
    uploadRealtimeManuallyShieldConfig({ state, getters }) {
      return new Promise((resolve, reject) => {
        function sliceArray(array, size) {
          var result = []
          for (var x = 0; x < Math.ceil(array.length / size); x++) {
            var start = x * size
            var end = start + size
            result.push(array.slice(start, end))
          }
          return result
        }
        const result = {
          shieldAreas: sliceArray(
            state.realtimeShieldInfo.manually.shield,
            120,
          ),
        }

        api
          .putShieldConfig(state.tarInfo.MachineId, getters.cameraId, {
            manuallyShield: JSON.stringify(result),
          })
          .then(resolve)
          .catch(reject)
      })
    },
    fetchRealtimeLineShieldConfig({ state, getters }) {
      return new Promise((resolve, reject) => {
        api
          .getShieldLineConfig(state.tarInfo.MachineId, getters.cameraId)
          .then(lineBlind => {
            state.realtimeShieldInfo.line = lineBlind
          })
      })
    },
    uploadRealtimeLineShieldConfig({ state, getters }) {
      return new Promise((resolve, reject) => {
        api
          .putShieldLineConfig(
            state.tarInfo.MachineId,
            getters.cameraId,
            state.realtimeShieldInfo.line,
          )
          .then(resolve)
          .catch(reject)
      })
    },
    fetchCameraInfo({ state }) {
      return new Promise((resolve, reject) => {
        const cameras = state.cameraList
        state.cameraInfo = {}
        if (cameras.length > 0) {
          cameras.forEach(cameraId => {
            api
              .getLabelers({ tarId: state.tarId, cameraId })
              .then(({ data }) => {
                if (data.labelers && data.labelers.length > 0) {
                  Vue.set(state.cameraInfo, cameraId, {
                    labeledNumber: data.labelers.length,
                    reviewedNumber: data.labelers.reduce(
                      (total, cur) =>
                        total + (cur.reviewTimeInt64 === 0 ? 0 : 1),
                      0,
                    ),
                  })
                } else {
                  Vue.set(state.cameraInfo, cameraId, {
                    labeledNumber: 0,
                    reviewedNumber: 0,
                  })
                }
              })
          })
        }
      })
    },
    fetchUserInfo({ state }) {
      return new Promise((resolve, reject) => {
        api
          .getUserInfo()
          .then(({ data }) => {
            state.userInfo = data.user
            resolve(data.user)
          })
          .catch(reject)
      })
    },
  },
})

// watch value and do some actions
Watcher.watch(storeX)

export default storeX
